import { useEffect, useState } from 'react';
import CloseXIcon from '../../assets/icons/punch/info-close-x.svg';
import MegaphoneImage from '../../assets/icons/punch/info-speaker.png';

const SHEET_CLOSE_ANIMATION_MS = 520;

export default function PunchInfoBottomSheet({
  isOpen,
  onClose,
  title,
  highlights = [],
  sections = [],
  notices = [],
  confirmLabel = '확인',
}) {
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [contentSnapshot, setContentSnapshot] = useState({
    title,
    highlights,
    sections,
    notices,
    confirmLabel,
  });

  useEffect(() => {
    if (!isOpen) return;
    setContentSnapshot({
      title,
      highlights,
      sections,
      notices,
      confirmLabel,
    });
  }, [isOpen, title, highlights, sections, notices, confirmLabel]);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      setIsClosing(false);
      return undefined;
    }
    if (!isMounted) return undefined;

    setIsClosing(true);
    const timer = window.setTimeout(() => {
      setIsMounted(false);
      setIsClosing(false);
    }, SHEET_CLOSE_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (!isMounted) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMounted, onClose]);

  useEffect(() => {
    if (!isMounted) return undefined;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverscroll = document.body.style.overscrollBehavior;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overscrollBehavior = prevBodyOverscroll;
      document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
    };
  }, [isMounted]);

  if (!isMounted) return null;

  const displayTitle = isOpen ? title : contentSnapshot.title;
  const displayHighlights = isOpen ? highlights : contentSnapshot.highlights;
  const displaySections = isOpen ? sections : contentSnapshot.sections;
  const displayNotices = isOpen ? notices : contentSnapshot.notices;
  const displayConfirmLabel = isOpen ? confirmLabel : contentSnapshot.confirmLabel;
  const iconSrc = displayTitle.includes('이용안내') ? MegaphoneImage : null;

  return (
    <div className="fixed inset-0 z-[60]">
      <button
        type="button"
        aria-label="안내 닫기"
        className={`absolute inset-0 bg-black/20 ${isClosing ? 'punch-guide-backdrop-out' : 'punch-guide-backdrop-in'}`}
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label={displayTitle}
        className={`absolute inset-x-0 bottom-0 mx-auto w-full max-w-[500px] overflow-hidden rounded-t-[24px] bg-gradient-to-t from-white from-[34.503%] to-[#DFD7FF] to-[91.708%] shadow-[0px_-8px_24px_rgba(0,0,0,0.18)] ${
          isClosing ? 'punch-guide-sheet-out' : 'punch-guide-sheet-in'
        }`}
      >
        <button
          type="button"
          aria-label="안내 닫기"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-6 w-6 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-gray05"
        >
          <img src={CloseXIcon} alt="" aria-hidden="true" className="h-4 w-4 object-contain" />
        </button>
        <div className="max-h-[68vh] overflow-y-auto px-[25px] pb-2 pt-12">
          <div className="mx-auto w-full">
            <div className="mb-5 w-full">
              {iconSrc ? (
                <div className="mb-2 flex h-10 w-10 items-center justify-center">
                  <img src={iconSrc} alt="" className="h-10 w-10 object-contain" />
                </div>
              ) : null}
              <h3 className="m-0 text-[22px] font-extrabold leading-[1.4] text-gray05">{displayTitle}</h3>
              {displayHighlights.length > 0 ? (
                <div className="mt-2 space-y-0 text-[14px] font-bold leading-[1.4] text-primary-purple-01">
                  {displayHighlights.map((line) => (
                    <p key={line} className="m-0">
                      {line}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="mx-auto w-full">
            <div className="text-[12px] font-normal leading-[1.6] text-gray04">
              {displaySections.map((section) => (
                <div key={section.title ?? section.items?.join('-')} className="mb-4">
                  {section.title ? <p className="m-0 font-bold">{section.title}</p> : null}
                  <div className={`${section.title ? 'mt-0.5' : ''}`}>
                    {section.items?.map((item) =>
                      section.bullet === false ? (
                        <p key={item} className="m-0">
                          {item}
                        </p>
                      ) : (
                        <p key={item} className="m-0">
                          • {item}
                        </p>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mx-auto w-full">
            {displayNotices.length > 0 ? (
              <div className="mt-1 space-y-0 text-[10px] font-normal leading-[1.4] text-gray04">
                {displayNotices.map((item) => (
                  <p key={item} className="m-0">
                    ※ {item}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex justify-center px-[15px] pb-6 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex h-[54px] w-full cursor-pointer items-center justify-center rounded-[8px] border-0 bg-[#1E1E1E] text-center text-[16px] font-bold leading-[1.4] text-white"
          >
            {displayConfirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}
